import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  makeStyles,
  withStyles,
} from '@material-ui/core';
import { Button } from '@redshelf/redshelf-ui';

import { PrintAccordion, PrintUsageBar, PageSelection } from './components';
import PrintUnavailableMessage from 'components/Print/PrintUnavailableMessage';
import {
  BookValuesContext,
  DRMValuesContext,
  NetworkStatusValuesContext,
  PrintModifiersContext,
  PrintValuesContext,
} from 'contexts';

import { calculateSelectionPercent } from 'utilities/printUtilities';
import {
  AriaLiveRegion,
  ConfirmationDialog as PrintConfirmationDialog,
} from 'components';
import { isNumber } from 'lodash';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      position: 'relative',
      overflowY: 'hidden',
    },
    scrollyBoy: {
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      maxHeight: `calc(100vh - 250px)`,

      [theme.breakpoints.down('sm')]: {
        maxHeight: `calc(100vh - 140px)`, // top bar + footer
        height: `calc(100vh - 142px)`,
      },
    },
    printActions: {
      padding: theme.spacing(2),
    },
    bottomButtonsRoot: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginRight: theme.spacing(2),
      padding: theme.spacing(2, 0),
    },
    bottomButtonsLeft: {
      marginLeft: theme.spacing(1),
    },
    printFooter: {
      position: 'sticky',
      bottom: 0,
      borderTop: `1px solid ${theme.palette.grey[300]}`,
    },
    notesNHighlightsCheck: {
      marginTop: theme.spacing(0.875),
    },
  };
});

const StyledCheckbox = withStyles((theme) => {
  return {
    root: {
      color: theme.palette.greyBlue[700],
      padding: theme.spacing(1.25),
      '&:hover': {
        backgroundColor: theme.palette.shelfBlue['50L'],
      },
    },
  };
})(Checkbox);

// TODO: how to handle not being able to print a11y-wise??
const BottomButtons = ({
  onCancel,
  printButtonOnClick,
  printButtonDisabled = false,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.bottomButtonsRoot}>
      <Button variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        disabled={printButtonDisabled}
        className={classes.bottomButtonsLeft}
        onClick={printButtonOnClick}
      >
        Print
      </Button>
    </div>
  );
};

export const PAGE_SELECTION_ERRORS = {
  ADD_BUTTON: 'addButton',
  START_PAGE: 'startPage',
  END_PAGE: 'endPage',
};

/**
 *  Top Level Print Component that handles data, state, and overall container styling
 */
const PrintMenu = ({ className, children, onClose, ...props }) => {
  const { printPercentMaximum } = useContext(DRMValuesContext);
  const { isOffline } = useContext(NetworkStatusValuesContext);
  const { book } = useContext(BookValuesContext);

  const {
    printHistory,
    pagesAlreadyPrinted,
    shouldPrintUserContent,
    pageRangesQueue,
    pageRangeStart,
    pageRangeEnd,
    displayText,
  } = useContext(PrintValuesContext);
  const {
    setShouldPrintUserContent,
    clearAllSelections,
    setPageRangeStart,
    setPageRangeEnd,
    onPrint,
    deleteQueue,
    addPageRange,
  } = useContext(PrintModifiersContext);

  const classes = useStyles();

  const pages = book.pages;

  const currentPercent = pagesAlreadyPrinted?.length / pages?.length ?? 0;
  const selectionPercent = calculateSelectionPercent(
    pageRangesQueue,
    pagesAlreadyPrinted,
    pages,
  );

  const drmInfo = useMemo(
    () => ({
      currentPercent,
      printPercentMaximum,
      selectionPercent,
    }),
    [currentPercent, printPercentMaximum, selectionPercent],
  );

  function handleCancel() {
    clearAllSelections();
    // handle canceling print jobs in another ticket
    onClose();
  }

  const shouldShowUnavailableMessage = isOffline || printPercentMaximum === 0;

  const [isPrintConfirmDialogOpen, setIsPrintConfirmDialogOpen] =
    useState(false);
  const [showExceedAllowanceError, setShowExceedAllowanceError] =
    useState(false);

  useEffect(() => {
    if (!showExceedAllowanceError) {
      return;
    }
    if (
      drmInfo.printPercentMaximum - drmInfo.currentPercent >
      drmInfo.selectionPercent
    ) {
      setShowExceedAllowanceError(false);
    }
  }, [drmInfo, showExceedAllowanceError]);

  const ariaLiveMessage = useRef();
  const resetAriaLiveMessage = useCallback(
    () => (ariaLiveMessage.current = undefined),
    [],
  );

  const [error, setError] = useState();
  const removeError = useCallback(() => setError(null), []);

  const getPageSelectionError = useCallback(
    (skipAddError = false) => {
      const startError = !isNumber(pageRangeStart) && 'Start Page';
      const endError = !isNumber(pageRangeEnd) && 'End Page';
      const addError =
        !skipAddError && !pageRangesQueue?.length
          ? 'Add selection to print.'
          : '';

      if (!startError && !endError && !addError) {
        return null;
      }

      let pagesError = '';
      let errorFocus = PAGE_SELECTION_ERRORS.ADD_BUTTON;

      if (startError && endError) {
        pagesError = `${startError} and ${endError} are required fields.`;
        errorFocus = PAGE_SELECTION_ERRORS.START_PAGE;
      } else if (startError || endError) {
        const fieldError = startError || endError;

        pagesError = `${fieldError} is a required field.`;
        errorFocus = startError
          ? PAGE_SELECTION_ERRORS.START_PAGE
          : PAGE_SELECTION_ERRORS.END_PAGE;
      }

      const newError = {
        message: `Error: ${pagesError && pagesError + ' '}${
          addError && addError
        }`,
        errorFocus,
      };

      setError(newError);

      return newError;
    },
    [pageRangeStart, pageRangeEnd, pageRangesQueue],
  );

  const onSetPageRangeStart = useCallback(
    (startPage) => {
      resetAriaLiveMessage();
      removeError();

      setPageRangeStart(startPage);
    },
    [removeError, resetAriaLiveMessage, setPageRangeStart],
  );

  const onSetPageRangeEnd = useCallback(
    (endPage) => {
      resetAriaLiveMessage();
      removeError();

      setPageRangeEnd(endPage);
    },
    [removeError, resetAriaLiveMessage, setPageRangeEnd],
  );

  const onAddPageRangeClick = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();

      resetAriaLiveMessage();

      if (getPageSelectionError(true)) {
        return;
      }

      removeError();
      const rangeLabel = addPageRange();
      ariaLiveMessage.current = (
        <>
          Added pages <span className="noTranslate">{rangeLabel}</span> to print
          queue.
        </>
      );
    },
    [getPageSelectionError, removeError, resetAriaLiveMessage, addPageRange],
  );

  const onDeleteClick = useCallback(
    (printJob) => {
      deleteQueue(printJob.id);

      ariaLiveMessage.current = (
        <>
          Removed pages{' '}
          <span className="noTranslate">{printJob?.displayText}</span> from
          print queue.
        </>
      );
    },
    [deleteQueue],
  );

  const printButtonOnClick = () => {
    resetAriaLiveMessage();

    if (!pageRangesQueue?.length && Boolean(getPageSelectionError())) {
      return;
    }

    removeError();
    drmInfo.printPercentMaximum - drmInfo.currentPercent >=
    drmInfo.selectionPercent
      ? setIsPrintConfirmDialogOpen(true)
      : setShowExceedAllowanceError(true);
  };

  return shouldShowUnavailableMessage ? (
    <PrintUnavailableMessage
      isOffline={isOffline}
      printEnabled={printPercentMaximum > 0}
      {...props}
    />
  ) : (
    <>
      <div className={clsx(classes.root, className)} {...props}>
        <AriaLiveRegion
          id="print-menu-message"
          message={ariaLiveMessage.current}
        />

        {/* wrapper for The Scrolly Boy */}
        <div className={classes.scrollyBoy}>
          <div className={classes.printActions}>
            <PageSelection
              pages={pages}
              pageRangeStart={pageRangeStart}
              pageRangeEnd={pageRangeEnd}
              onSetPageRangeStart={onSetPageRangeStart}
              onSetPageRangeEnd={onSetPageRangeEnd}
              onAddPageRangeClick={onAddPageRangeClick}
              error={error}
            />
            <PrintAccordion
              printJobsList={pageRangesQueue}
              accordionName="queue"
              totalPages={pages.length}
              onDeleteClick={onDeleteClick}
            />
            {Boolean(pageRangesQueue?.[0]) && (
              <FormControlLabel
                className={classes.notesNHighlightsCheck}
                control={
                  <StyledCheckbox
                    checked={shouldPrintUserContent}
                    color="primary"
                    disabled={null}
                    name={'printUserContent'}
                    onChange={() => {
                      resetAriaLiveMessage();
                      setShouldPrintUserContent(!shouldPrintUserContent);
                    }}
                  />
                }
                label={'Include my notes and highlights in the PDF'}
              />
            )}
            <PrintAccordion
              printJobsList={printHistory}
              totalPages={pages.length}
              accordionName="history"
            />
          </div>
          <Divider />
          <PrintUsageBar
            drmInfo={drmInfo}
            showExceedAllowanceError={showExceedAllowanceError}
          />
          <Divider />
        </div>
        <div className={classes.printFooter}>
          <BottomButtons
            onCancel={handleCancel}
            printButtonOnClick={printButtonOnClick}
          />
        </div>
      </div>
      <PrintConfirmationDialog
        title="Are you sure you want to continue?"
        message={
          <>
            You are about to print page
            {pageRangesQueue?.length === 1 &&
            pageRangesQueue[0].pages?.length === 1
              ? ''
              : 's'}{' '}
            <span className="noTranslate">{displayText}.</span> After doing
            this, you will have{' '}
            <span className="noTranslate">
              {(
                (drmInfo.printPercentMaximum -
                  drmInfo.currentPercent -
                  drmInfo.selectionPercent) *
                100
              ).toFixed(1)}
              %
            </span>{' '}
            remaining of your print allotment.
          </>
        }
        cancel={{
          label: 'Cancel',
          action: () => {
            setIsPrintConfirmDialogOpen(false);
          },
        }}
        confirm={{
          label: 'Continue',
          action: () => {
            onPrint();
            setIsPrintConfirmDialogOpen(false);
          },
        }}
        isOpen={isPrintConfirmDialogOpen}
        labelledById="print-confirmation"
      ></PrintConfirmationDialog>
    </>
  );
};

PrintMenu.propTypes = {
  children: PropTypes.node,
  classes: PropTypes.object,
  className: PropTypes.string,
};

export default PrintMenu;
